/**
 * RTWO Event Bus — Metadata Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type { EventId, MetadataId, RTWOEventMetadata } from '../domain-models';
import type { IMetadataService, MetadataResult } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class MetadataService extends BaseEBusService implements IMetadataService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.METADATA_SERVICE;
  readonly serviceName = 'Metadata Service';
  readonly description =
    'Manages event metadata. No metadata until real events flow from engine sources.';
  readonly integrations = ['Event Registry', 'All RTWO Engines'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async createMetadata(
    _eventId: EventId,
    _schemaVersion: string,
    _correlationId: string | null
  ): Promise<MetadataResult> {
    return {
      success: false,
      metadataId: null,
      error: 'Metadata Service Not Active — Cannot create metadata until real events are flowing.',
      isPlaceholder: true,
      source: 'Event Bus — Metadata Service Not Active',
    };
  }

  getMetadataByEventId(eventId: EventId): RTWOEventMetadata | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.metadata.findByEventId(eventId);
  }

  getMetadata(metadataId: MetadataId): RTWOEventMetadata | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.metadata.findById(metadataId);
  }
}
